import { useI18n } from "@/i18n/client";
import { SearchSuggestionSkeleton } from "@/components/UI/Skeleton/SearchSkeleton";
import styles from "./SearchSuggestions.module.scss";

interface searchSuggestionsProps {
	isLoading: boolean;
	searchEntry: string;
	suggestions: string[];
	manageSuggestionClick: (suggestion: string) => void;
	manageCloseDropDown: () => void;
	maxLimit: number;
}

const SearchSuggestions = ({
	isLoading,
	searchEntry,
	suggestions,
	manageSuggestionClick,
	manageCloseDropDown,
	maxLimit = 4,
}: searchSuggestionsProps) => {
	const t = useI18n();

	return (
		<div className={styles.suggestions}>
			<div className={styles.header}>
				{t("search.suggestions")}
				<button
					className={styles.close}
					type='button'
					onClick={() => manageCloseDropDown()}
					aria-label={t("search.close")}
				>
					{t("search.close")}
				</button>
			</div>
			<div className={styles.list}>
				{!isLoading &&
					suggestions?.map((suggestion: string, index) => {
						if (!suggestion) return null;

						// Max of 4 suggestions
						if (index + 1 > maxLimit) return null;

						// Split the suggestion itnto two sections, one that matched the search query and the rest
						const [matchingPart, remainingPart] = splitMatch(
							searchEntry,
							suggestion,
						);

						return (
							<button
								key={suggestion}
								type='button'
								className={styles.item}
								onClick={() => manageSuggestionClick(suggestion)}
							>
								<span className={styles.matched}>{matchingPart}</span>
								{remainingPart}
							</button>
						);
					})}
				{searchEntry && !isLoading && !suggestions.length && (
					<span className={styles.noResults}>
						{t("search.noResults.noSuggestions")}
					</span>
				)}
				{isLoading && <SearchSuggestionSkeleton />}
			</div>
		</div>
	);
};

export default SearchSuggestions;

function splitMatch(searchTerm: string, fullString: string) {
	const index = fullString.toLowerCase().indexOf(searchTerm.toLowerCase());

	if (index === -1) {
		return [fullString, ""]; // No match found, return the full string as is
	}

	const matchingPart = fullString.substring(index, index + searchTerm.length);
	const remainingPart = fullString.substring(index + searchTerm.length);

	return [matchingPart, remainingPart];
}
